import React, { useState, useEffect, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import Loading from '../loading/Loading'
import AdminBookModal from '../modals/AdminBookModal'
import AdminPaperModal from '../modals/AdminPaperModal'
import AdminPolicyModal from '../modals/AdminPolicyModal'
import AdminCourseModal from '../modals/AdminCourseModal'
import AdminVideoModal from '../modals/AdminVideoModal'
import AdminPodcastModal from '../modals/AdminPodcastModal'
import AdminCodeNotebookModal from '../modals/AdminCodeNotebookModal'

import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import Container from '@mui/material/Container'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Pagination from '@mui/material/Pagination'
import Button from '@mui/material/Button'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import QuestionMarkIcon from '@mui/icons-material/QuestionMark'
import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import FormLabel from '@mui/material/FormLabel'
import FormControlLabel from '@mui/material/FormControlLabel'
import RadioGroup from '@mui/material/RadioGroup'
import Radio from '@mui/material/Radio'
import Grid from '@mui/material/Grid'
import Chip from '@mui/material/Chip'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'

import resourcesService from '../../services/resourceService'
import { parseErrorMessage } from '../../services/utils.js/errorParser'
import { setSuccessNotification, setErrorNotification } from '../../reducers/notificationReducer'

import { AdminManage } from '../styles'

import { PAGES } from '../navigation/routes'

import { initializeBooks, searchBooks, deleteBook } from '../../reducers/booksReducer'
import { initializeCourses, searchCourses, deleteCourse } from '../../reducers/coursesReducer'
import { initializePapers, searchPapers, deletePaper } from '../../reducers/papersReducer'
import { initializePolicies, searchPolicies, deletePolicy } from '../../reducers/policiesReducer'
import { initializePodcasts, searchPodcasts, deletePodcast } from '../../reducers/podcastsReducer'
import { initializeVideos, searchVideos, deleteVideo } from '../../reducers/videosReducer'
import { initializeCodeNotebooks, searchCodeNotebooks, deleteCodeNotebook } from '../../reducers/codenotebooksReducer'

const RESOURCE_TYPES = [
    {
        key: 'Book',
        label: 'Books',
        stateKey: 'books',
        initializeAction: initializeBooks,
        searchAction: searchBooks,
        deleteAction: deleteBook,
        ModalComponent: AdminBookModal,
    },
    {
        key: 'Course',
        label: 'Courses',
        stateKey: 'courses',
        initializeAction: initializeCourses,
        searchAction: searchCourses,
        deleteAction: deleteCourse,
        ModalComponent: AdminCourseModal,
    },
    {
        key: 'Paper',
        label: 'Papers',
        stateKey: 'papers',
        initializeAction: initializePapers,
        searchAction: searchPapers,
        deleteAction: deletePaper,
        ModalComponent: AdminPaperModal,
    },
    {
        key: 'Policy',
        label: 'Policies',
        stateKey: 'policies',
        initializeAction: initializePolicies,
        searchAction: searchPolicies,
        deleteAction: deletePolicy,
        ModalComponent: AdminPolicyModal,
    },
    {
        key: 'Podcast',
        label: 'Podcasts',
        stateKey: 'podcasts',
        initializeAction: initializePodcasts,
        searchAction: searchPodcasts,
        deleteAction: deletePodcast,
        ModalComponent: AdminPodcastModal,
    },
    {
        key: 'Video',
        label: 'Videos',
        stateKey: 'videos',
        initializeAction: initializeVideos,
        searchAction: searchVideos,
        deleteAction: deleteVideo,
        ModalComponent: AdminVideoModal,
    },
    {
        key: 'CodeNotebook',
        label: 'Code Notebooks',
        stateKey: 'codenotebooks',
        initializeAction: initializeCodeNotebooks,
        searchAction: searchCodeNotebooks,
        deleteAction: deleteCodeNotebook,
        ModalComponent: AdminCodeNotebookModal,
    },
]

const buildAdminQuery = (title = '', approvalStatus = 'all') => {
    const params = []

    if (title.trim()) {
        params.push(`title=${encodeURIComponent(title.trim())}`)
    }

    if (approvalStatus === 'approved') {
        params.push('approved=true')
    } else if (approvalStatus === 'pending') {
        params.push('approved=false')
    }

    return params.join('&')
}

const ManageResources = () => {
    const [titleInput, setTitleInput] = useState('')
    const [filter, setFilter] = useState('')
    const [approvalStatus, setApprovalStatus] = useState('all')
    const [selectedType, setSelectedType] = useState('Book')
    const [hasRequested, setHasRequested] = useState(false)

    const user = useSelector(state => state.user)
    const dispatch = useDispatch()

    const [page, setPage] = useState(1)

    const activeConfig = RESOURCE_TYPES.find(type => type.key === selectedType) || RESOURCE_TYPES[0]
    const activeData = useSelector(state => state[activeConfig.stateKey])
    const activeResources = activeData?.results || []
    const totalPages = activeData?.total_pages || 1
    const isLoading = activeData?.loading || false

    const refreshCurrentPage = useCallback((pageToLoad = page) => {
        const query = buildAdminQuery(filter, approvalStatus)

        if (query) {
            dispatch(activeConfig.searchAction(query, pageToLoad))
        } else {
            dispatch(activeConfig.initializeAction(pageToLoad))
        }
    }, [dispatch, activeConfig, filter, approvalStatus, page])

    useEffect(() => {
        // RequireStaff (App.js) guarantees this component only mounts for a
        // loaded, staff user — this check just guards against `user` being
        // momentarily stale on the very first effect run.
        if (user?.staff) {
            setHasRequested(true)
            refreshCurrentPage(page)
        }
    }, [user, page, refreshCurrentPage])

    const handleSearchSubmit = (event) => {
        if (event && event.preventDefault) event.preventDefault()
        setFilter(titleInput)
        setPage(1)
    }

    // PATCH only `approved`: the row in the list is a cached copy, so re-PUTting it would
    // revert any edit made since the list loaded, and for Course/Podcast/Video would send
    // an empty sub-resource list that the backend reads as "delete every child" (issue #353).
    const setApproval = async (resource, approved, prompt) => {
        if (!window.confirm(prompt)) return
        try {
            await resourcesService.patch(resource.id, { approved })
            dispatch(setSuccessNotification(approved ? 'Resource approved' : 'Resource unapproved'))
        } catch (error) {
            const message = parseErrorMessage(error, 'Update failed')
            dispatch(setErrorNotification(`Update failed: ${message}`))
        }
        refreshCurrentPage(page)
    }

    const handleApprove = (resource) =>
        setApproval(resource, true, 'Are you sure you want to approve this resource?')

    const handleDisapprove = (resource) =>
        setApproval(resource, false, 'Are you sure you want to unapprove this resource?')

    const handleDelete = async (resource) => {
        if (window.confirm('Are you sure you want to delete this resource? This action cannot be undone.')) {
            await dispatch(activeConfig.deleteAction(resource.id, user.staff))

            refreshCurrentPage(page)
        }
    }

    if ((!hasRequested && !activeResources.length) || isLoading) {
        return (
            <Container {...AdminManage.loading.props} sx={AdminManage.loading.style}>
                <Loading />
            </Container>
        )
    }

    const ModalComponent = activeConfig.ModalComponent

    return (
        <Container sx={AdminManage.container}>
            <Typography {...AdminManage.title} gutterBottom>
                {PAGES.manage_resources.title}
            </Typography>
            <Typography {...AdminManage.filterTitle} gutterBottom>Resource Type</Typography>
            <Tabs
                value={selectedType}
                onChange={(event, value) => {
                    setSelectedType(value)
                    setPage(1)
                }}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ mb: 2 }}
            >
                {RESOURCE_TYPES.map(type => (
                    <Tab key={type.key} value={type.key} label={type.label} />
                ))}
            </Tabs>
            {(activeData.count > 0)
                ? <>
                    <Typography {...AdminManage.filterTitle} gutterBottom>Filters</Typography>

                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Box
                                component="form"
                                onSubmit={handleSearchSubmit}
                                sx={{ display: 'flex', gap: 2, marginBottom: '20px' }}
                            >
                                <TextField
                                    fullWidth
                                    value={titleInput}
                                    onChange={e => setTitleInput(e.target.value)}
                                    label="Filter By Title"
                                />
                                <Button type="submit" variant="contained">
                                    Search
                                </Button>
                            </Box>
                        </Grid>
                        <Grid item xs={12}>
                            <FormLabel>Approval Status</FormLabel>
                            <RadioGroup
                                row
                                value={approvalStatus}
                                onChange={e => {
                                    setApprovalStatus(e.target.value)
                                    setPage(1)
                                }}
                                sx={{ mb: 1 }}
                            >
                                <FormControlLabel value="all" control={<Radio />} label="All" />
                                <FormControlLabel value="approved" control={<Radio />} label="Approved" />
                                <FormControlLabel value="pending" control={<Radio />} label="Pending" />
                            </RadioGroup>
                        </Grid>
                    </Grid>

                    <TableContainer component={Paper}>
                        <Table sx={AdminManage.table} aria-label="simple table">
                            <TableHead>
                                <TableRow>
                                    <TableCell>
                                        Name
                                    </TableCell>
                                    <TableCell {...AdminManage.columnAlign}>Status</TableCell>
                                    <TableCell {...AdminManage.columnAlign}>Edit</TableCell>
                                    <TableCell {...AdminManage.columnAlign}>Approve / Unapprove</TableCell>
                                    <TableCell {...AdminManage.columnAlign}>Delete</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {activeResources.map(resource => (
                                    <TableRow
                                        key={`${resource.type}${resource.id}`}
                                        sx={AdminManage.tableRow}
                                    >
                                        <TableCell {...AdminManage.tableCell.props} sx={AdminManage.tableCell.style}>
                                            {resource.title}
                                        </TableCell>
                                        <TableCell {...AdminManage.columnAlign}>
                                            <Chip
                                                label={resource.approved ? 'Approved' : 'Pending'}
                                                color={resource.approved ? 'success' : 'warning'}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell {...AdminManage.columnAlign}>
                                            <ModalComponent {...resource} onChanged={() => refreshCurrentPage(page)} />
                                        </TableCell>
                                        <TableCell {...AdminManage.columnAlign}>
                                            {resource.approved ? (
                                                <Button {...AdminManage.reject.props} sx={AdminManage.reject.style} onClick={() => handleDisapprove(resource)}>
                                                    <CloseIcon />&nbsp;Unapprove
                                                </Button>
                                            ) : (
                                                <Button {...AdminManage.approve.props} sx={AdminManage.approve.style} onClick={() => handleApprove(resource)}>
                                                    <CheckIcon />&nbsp;Approve
                                                </Button>
                                            )}
                                        </TableCell>
                                        <TableCell {...AdminManage.columnAlign}>
                                            <Button {...AdminManage.reject.props} sx={AdminManage.reject.style} onClick={() => handleDelete(resource)}>
                                                <DeleteForeverIcon />&nbsp;Delete
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Pagination sx={AdminManage.pagination} count={totalPages} page={page} onChange={(event, value) => setPage(value)} />
                </>
                : <Box sx={AdminManage.noResources}>
                    <QuestionMarkIcon sx={AdminManage.noResourceIcon} />
                    <Typography {...AdminManage.noResourceText} gutterBottom>
                        There are no resources to manage
                    </Typography>
                </Box>
            }
        </Container>
    )
}

export default ManageResources
